import LiquidText, { liquidTone } from '../components/LiquidText';
import { LiquidDialog } from '../components/LiquidUI';
import DeleteIcon from '../components/DeleteIcon';
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import AnexosSgiModal from '../components/AnexosSgiModal';

/* ============================================================
   PLANO DE FORMAÇÃO (SGI — RG.14) — por ano
   Ações de formação planeadas (meses, duração, orçamento) e
   avaliação da eficácia por formando, fiel ao RG.14.
   ============================================================ */

const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const ESTADOS_ACAO = {
    planeada: { label: 'Planeada', bg: '#fef9c3', fg: '#854d0e' },
    realizada: { label: 'Realizada', bg: '#dcfce7', fg: '#166534' },
    cancelada: { label: 'Cancelada', bg: '#fee2e2', fg: '#991b1b' },
};
const EFICACIA_COR = {
    'Eficaz': { bg: '#dcfce7', fg: '#166534' },
    'Não Eficaz': { bg: '#fee2e2', fg: '#991b1b' },
};

const mesesArr = (csv) => String(csv || '').split(',').map(m => parseInt(m, 10)).filter(m => m >= 1 && m <= 12);
const fmtNum = (v) => (v == null || v === '' ? '—' : parseFloat(v).toLocaleString('pt-PT'));

/* ---------- Destinatários nomeados ----------
   O RG.14 tinha "Todos os Colaboradores" na coluna dos destinatários e só as
   folhas de eficácia nomeavam pessoas. Com o destinatário genérico não há
   forma de responder a quem devia ter tido a formação e não teve — que é o
   que a auditoria pergunta quando amostra uma ação. */
function DestinatariosPicker({ acao, onClose, onGravado }) {
    const [todos, setTodos] = useState([]);
    const [sel, setSel] = useState(() => new Set((acao.destinatarios_nomes || []).map(d => d.trabalhador_id)));
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [filtro, setFiltro] = useState('');

    useEffect(() => {
        api.get('/sistema-integrado/formacao/trabalhadores')
            .then(r => setTodos(r.data || []))
            .catch(e => console.error(e))
            .finally(() => setLoading(false));
    }, []);

    const alterna = (id) => setSel(prev => {
        const n = new Set(prev);
        if (n.has(id)) n.delete(id); else n.add(id);
        return n;
    });

    const visiveis = todos.filter(t => !filtro || t.nome.toLowerCase().includes(filtro.toLowerCase()));

    const gravar = async () => {
        setSaving(true);
        try {
            await api.put(`/sistema-integrado/formacao/${acao.id}/destinatarios`, { trabalhadores: Array.from(sel) });
            onGravado(); onClose();
        } catch { alert('Erro ao guardar destinatários'); }
        finally { setSaving(false); }
    };

    return (
        <LiquidDialog data-sgi-dialog="true" onClose={onClose} style={overlay} onClick={onClose}>
            <div style={{ ...modal, maxWidth: 620 }} onClick={e => e.stopPropagation()}>
                <div data-sgi-dialog-heading="true" style={modalHead}>
                    <h3 style={{ margin: 0 }}>Destinatários — ação {acao.numero || ''}</h3>
                    <button data-liquid-close="true" aria-label="Fechar" onClick={onClose} style={closeBtn}><LiquidText>✕</LiquidText></button>
                </div>
                <div style={{ color: '#334155', fontSize: '0.88rem', fontWeight: 600, marginBottom: 6 }}>{acao.designacao}</div>
                <p style={{ margin: '0 0 12px', fontSize: '0.82rem', color: '#64748b' }}>
                    Nomear cada trabalhador em vez de escrever «todos os colaboradores» é o que
                    permite depois comparar quem estava previsto com quem esteve presente.
                </p>
                <div data-liquid-actions="true" style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                    <input value={filtro} onChange={e => setFiltro(e.target.value)} placeholder="Procurar…" style={{ ...inp, flex: 1 }} />
                    <button data-liquid-secondary="true" onClick={() => setSel(new Set(todos.map(t => t.id)))} style={btnSec}>Todos</button>
                    <button data-liquid-secondary="true" onClick={() => setSel(new Set())} style={btnSec}>Nenhum</button>
                </div>
                {loading ? <p style={{ color: '#718096' }}>A carregar…</p> : (
                    <div style={{ maxHeight: 340, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                        {visiveis.map(t => (
                            <label key={t.id} style={{
                                display: 'flex', alignItems: 'center', gap: 10, padding: '7px 12px',
                                borderBottom: '1px solid #f8fafc', cursor: 'pointer',
                                background: sel.has(t.id) ? '#eff6ff' : '#fff',
                            }}>
                                <input type="checkbox" checked={sel.has(t.id)} onChange={() => alterna(t.id)} />
                                <span style={{ fontWeight: 600, fontSize: '0.86rem' }}>{t.nome}</span>
                                <span style={{ color: '#94a3b8', fontSize: '0.78rem', marginLeft: 'auto' }}>{t.categoria_profissional || ''}</span>
                            </label>
                        ))}
                    </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14 }}>
                    <span style={{ fontSize: '0.82rem', color: '#64748b' }}>{sel.size} selecionados</span>
                    <div data-liquid-actions="true" style={{ display: 'flex', gap: 8 }}>
                        <button data-liquid-secondary="true" onClick={onClose} style={btnSec}>Cancelar</button>
                        <button data-liquid-primary="true" onClick={gravar} disabled={saving} style={{ ...btnPri, opacity: saving ? 0.6 : 1 }}>{saving ? 'A guardar…' : 'Guardar'}</button>
                    </div>
                </div>
            </div>
        </LiquidDialog>
    );
}

/* ---------- Criar / editar ação ---------- */
function AcaoModal({ acao, ano, onClose, onSaved }) {
    const novo = !acao;
    const [form, setForm] = useState(() => ({
        numero: acao?.numero ?? '',
        designacao: acao?.designacao || '',
        objetivos: acao?.objetivos || '',
        duracao_horas: acao?.duracao_horas ?? '',
        destinatarios: acao?.destinatarios || '',
        tipo: acao?.tipo || 'Externa',
        entidade_formadora: acao?.entidade_formadora || '',
        num_formandos: acao?.num_formandos ?? '',
        horas_totais: acao?.horas_totais ?? '',
        orcamento: acao?.orcamento ?? '',
        estado: acao?.estado || 'planeada',
        observacoes: acao?.observacoes || '',
        // Colunas Y e Z do RG.14. O P_AF_02 remete os critérios da avaliação
        // da eficácia para o plano de formação, por isso ficam aqui na ação.
        avalia_eficacia: acao?.avalia_eficacia !== false,
        criterios_eficacia: acao?.criterios_eficacia || '',
    }));
    const [meses, setMeses] = useState(() => new Set(mesesArr(acao?.meses)));
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');
    const upd = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

    const toggleMes = (m) => setMeses(prev => {
        const next = new Set(prev);
        if (next.has(m)) next.delete(m); else next.add(m);
        return next;
    });

    const guardar = async () => {
        if (!form.designacao.trim()) { setErr('Indique a designação da ação.'); return; }
        setSaving(true); setErr('');
        try {
            const payload = { ...form, ano, meses: Array.from(meses).sort((a, b) => a - b).join(',') };
            if (novo) await api.post('/sistema-integrado/formacao', payload);
            else await api.put(`/sistema-integrado/formacao/${acao.id}`, { ...acao, ...payload });
            onSaved(); onClose();
        } catch (e) { setErr(e.response?.data?.error || 'Erro ao guardar'); }
        finally { setSaving(false); }
    };

    return (
        <LiquidDialog data-sgi-dialog="true" onClose={onClose} style={overlay} onClick={onClose}>
            <div style={{ ...modal, maxWidth: 720 }} onClick={e => e.stopPropagation()}>
                <div data-sgi-dialog-heading="true" style={modalHead}>
                    <h3 style={{ margin: 0 }}>{novo ? `Nova ação de formação (${ano})` : `Editar ação ${acao.numero || ''}`}</h3>
                    <button data-liquid-close="true" aria-label="Fechar" onClick={onClose} style={closeBtn}><LiquidText>✕</LiquidText></button>
                </div>

                <div style={grid3}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Nº ação</label>
                        <input type="number" value={form.numero} onChange={e => upd('numero', e.target.value)} style={{ ...inp, width: '100%' }} />
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Tipo</label>
                        <select value={form.tipo} onChange={e => upd('tipo', e.target.value)} style={{ ...inp, width: '100%' }}>
                            <option value="Externa">Externa</option>
                            <option value="Interna">Interna</option>
                        </select>
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Estado</label>
                        <select value={form.estado} onChange={e => upd('estado', e.target.value)} style={{ ...inp, width: '100%' }}>
                            {Object.entries(ESTADOS_ACAO).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                        </select>
                    </div>
                </div>
                <div style={{ marginBottom: 10 }}>
                    <label data-liquid-label="true" style={lbl}>Designação da ação *</label>
                    <input value={form.designacao} onChange={e => upd('designacao', e.target.value)} style={{ ...inp, width: '100%' }} />
                </div>
                <div style={{ marginBottom: 10 }}>
                    <label data-liquid-label="true" style={lbl}>Objetivos</label>
                    <textarea value={form.objetivos} onChange={e => upd('objetivos', e.target.value)} rows={2} style={{ ...inp, width: '100%', resize: 'vertical' }} />
                </div>
                <div style={grid2}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Destinatários</label>
                        <input value={form.destinatarios} onChange={e => upd('destinatarios', e.target.value)} placeholder="ex.: Todos os colaboradores" style={{ ...inp, width: '100%' }} />
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Entidade formadora / formador</label>
                        <input value={form.entidade_formadora} onChange={e => upd('entidade_formadora', e.target.value)} style={{ ...inp, width: '100%' }} />
                    </div>
                </div>
                <div style={grid4}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Duração (h)</label>
                        <input type="number" step="0.5" value={form.duracao_horas} onChange={e => upd('duracao_horas', e.target.value)} style={{ ...inp, width: '100%' }} />
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Nº formandos</label>
                        <input type="number" value={form.num_formandos} onChange={e => upd('num_formandos', e.target.value)} style={{ ...inp, width: '100%' }} />
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Horas totais (h)</label>
                        <input type="number" step="0.5" value={form.horas_totais} onChange={e => upd('horas_totais', e.target.value)} style={{ ...inp, width: '100%' }} />
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Orçamento (€)</label>
                        <input type="number" step="0.01" value={form.orcamento} onChange={e => upd('orcamento', e.target.value)} style={{ ...inp, width: '100%' }} />
                    </div>
                </div>
                <div style={{ marginBottom: 10 }}>
                    <label data-liquid-label="true" style={lbl}>Planeamento — meses do ano</label>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {MESES.map((m, i) => (
                            <button key={m} type="button" onClick={() => toggleMes(i + 1)}
                                style={{
                                    padding: '5px 10px', borderRadius: 8, fontSize: '0.78rem', cursor: 'pointer',
                                    border: `1px solid ${meses.has(i + 1) ? '#3182ce' : '#cbd5e1'}`,
                                    background: meses.has(i + 1) ? '#3182ce' : '#fff',
                                    color: meses.has(i + 1) ? '#fff' : '#334155', fontWeight: 600,
                                }}>
                                {m}
                            </button>
                        ))}
                    </div>
                </div>
                <div style={{ marginBottom: 10, padding: '12px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: form.avalia_eficacia ? 10 : 0 }}>
                        <input type="checkbox" checked={form.avalia_eficacia}
                            onChange={e => upd('avalia_eficacia', e.target.checked)} />
                        <span style={{ fontWeight: 600, fontSize: '0.86rem', color: '#334155' }}>Avalia-se a eficácia desta ação?</span>
                    </label>
                    {form.avalia_eficacia && (
                        <>
                            <label data-liquid-label="true" style={lbl}>Critérios de avaliação da eficácia</label>
                            <textarea value={form.criterios_eficacia} onChange={e => upd('criterios_eficacia', e.target.value)}
                                rows={2} placeholder="Ex.: Avaliar a atuação dos colaboradores em simulacros de emergência médica"
                                style={{ ...inp, width: '100%', resize: 'vertical' }} />
                            <p style={{ margin: '6px 0 0', fontSize: '0.75rem', color: '#94a3b8' }}>
                                O P_AF_02 exige que os critérios estejam estabelecidos no plano de formação.
                                É contra isto que cada formando é avaliado.
                            </p>
                        </>
                    )}
                </div>

                <div style={{ marginBottom: 6 }}>
                    <label data-liquid-label="true" style={lbl}>Observações</label>
                    <textarea value={form.observacoes} onChange={e => upd('observacoes', e.target.value)} rows={2} style={{ ...inp, width: '100%', resize: 'vertical' }} />
                </div>

                {err && <div style={{ color: '#e53e3e', margin: '10px 0', fontSize: '0.85rem' }}>{err}</div>}
                <div data-liquid-actions="true" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
                    <button data-liquid-secondary="true" onClick={onClose} style={btnSec}>Cancelar</button>
                    <button data-liquid-primary="true" onClick={guardar} disabled={saving} style={{ ...btnPri, opacity: saving ? 0.6 : 1 }}>{saving ? 'A guardar…' : 'Guardar'}</button>
                </div>
            </div>
        </LiquidDialog>
    );
}

/* ---------- Eficácia (RG.14 — folha por ação) ---------- */
function EficaciaModal({ acao, onClose, onChanged }) {
    const [info, setInfo] = useState({
        data_realizacao: acao.data_realizacao || '',
        responsavel_avaliacao: acao.responsavel_avaliacao || '',
        previsao_avaliacao: acao.previsao_avaliacao || '',
        data_avaliacao: acao.data_avaliacao || '',
        // Editável aqui e não só no plano: é a avaliar que se percebe se o
        // critério escrito à partida serve, e obrigar a fechar a janela para o
        // corrigir fazia perder o contexto todo.
        criterios_eficacia: acao.criterios_eficacia || '',
    });
    const [formandos, setFormandos] = useState(acao.formandos || []);
    const [novoNome, setNovoNome] = useState('');
    const [err, setErr] = useState('');

    const guardarInfo = async () => {
        try {
            await api.put(`/sistema-integrado/formacao/${acao.id}`, { ...acao, ...info });
            onChanged();
        } catch (e) { setErr(e.response?.data?.error || 'Erro ao guardar dados da avaliação'); }
    };

    const adicionarFormando = async () => {
        const nome = novoNome.trim();
        if (!nome) return;
        try {
            const r = await api.post(`/sistema-integrado/formacao/${acao.id}/formandos`, { nome });
            setFormandos(prev => [...prev, r.data]);
            setNovoNome('');
            onChanged();
        } catch (e) { setErr(e.response?.data?.error || 'Erro ao adicionar formando'); }
    };

    const atualizarFormando = async (f, campos) => {
        try {
            const r = await api.put(`/sistema-integrado/formacao/formandos/${f.id}`, { ...f, ...campos });
            setFormandos(prev => prev.map(x => x.id === f.id ? r.data : x));
            onChanged();
        } catch { alert('Erro ao atualizar formando'); }
    };

    const removerFormando = async (f) => {
        if (!window.confirm(`Remover "${f.nome}" desta ação?`)) return;
        try {
            await api.delete(`/sistema-integrado/formacao/formandos/${f.id}`);
            setFormandos(prev => prev.filter(x => x.id !== f.id));
            onChanged();
        } catch { alert('Erro ao remover formando'); }
    };

    return (
        <LiquidDialog data-sgi-dialog="true" onClose={onClose} style={overlay} onClick={onClose}>
            <div style={{ ...modal, maxWidth: 760 }} onClick={e => e.stopPropagation()}>
                <div data-sgi-dialog-heading="true" style={modalHead}>
                    <h3 style={{ margin: 0 }}>Avaliação da eficácia — Ação {acao.numero || ''}</h3>
                    <button data-liquid-close="true" aria-label="Fechar" onClick={onClose} style={closeBtn}><LiquidText>✕</LiquidText></button>
                </div>
                <div style={{ color: '#64748b', fontSize: '0.88rem', marginBottom: 10 }}>
                    <strong>{acao.designacao}</strong>{acao.objetivos ? ` — ${acao.objetivos}` : ''}
                </div>

                <div style={grid3}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Data de realização</label>
                        <input value={info.data_realizacao} onChange={e => setInfo(p => ({ ...p, data_realizacao: e.target.value }))}
                            onBlur={guardarInfo} placeholder="ex.: 01.04.2025" style={{ ...inp, width: '100%' }} />
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Responsável avaliação</label>
                        <input value={info.responsavel_avaliacao} onChange={e => setInfo(p => ({ ...p, responsavel_avaliacao: e.target.value }))}
                            onBlur={guardarInfo} style={{ ...inp, width: '100%' }} />
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Previsão avaliação eficácia</label>
                        <input value={info.previsao_avaliacao} onChange={e => setInfo(p => ({ ...p, previsao_avaliacao: e.target.value }))}
                            onBlur={guardarInfo} placeholder="ex.: 31.01.2026 ou simulacro" style={{ ...inp, width: '100%' }} />
                    </div>
                </div>

                {/* O critério vem do plano (RG.14, coluna Z). Aparece aqui porque é
                    contra ele que se decide Eficaz / Não Eficaz de cada formando —
                    sem o ter à frente, a avaliação fica ao critério de cada um. */}
                <div style={{
                    margin: '0 0 12px', padding: '10px 14px', borderRadius: 8,
                    background: info.criterios_eficacia ? '#eff6ff' : '#fef3c7',
                    border: `1px solid ${info.criterios_eficacia ? '#bfdbfe' : '#fde68a'}`,
                }}>
                    <label htmlFor="crit-eficacia" style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: 4 }}>
                        Critério de avaliação da eficácia
                    </label>
                    <textarea id="crit-eficacia" rows={2}
                        value={info.criterios_eficacia}
                        onChange={e => setInfo(p => ({ ...p, criterios_eficacia: e.target.value }))}
                        onBlur={guardarInfo}
                        placeholder="ex.: Verificação em obra do cumprimento das regras transmitidas no acolhimento"
                        style={{
                            width: '100%', resize: 'vertical', minHeight: 44,
                            fontFamily: 'inherit', fontSize: '0.88rem', lineHeight: 1.45,
                            color: info.criterios_eficacia ? '#1e3a8a' : '#92400e',
                            background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6,
                            padding: '7px 10px',
                        }} />
                    {!info.criterios_eficacia && (
                        <div style={{ fontSize: '0.78rem', color: '#92400e', marginTop: 5 }}>
                            O P_AF_02 exige que o critério esteja estabelecido antes de avaliar.
                        </div>
                    )}
                </div>

                <div style={{ marginBottom: 12 }}>
                    <label data-liquid-label="true" style={lbl}>Data em que a avaliação foi feita</label>
                    <input value={info.data_avaliacao} onChange={e => setInfo(p => ({ ...p, data_avaliacao: e.target.value }))}
                        onBlur={guardarInfo} placeholder="ex.: 09.07.2025" style={{ ...inp, width: 220 }} />
                </div>

                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem', marginBottom: 10 }}>
                    <thead>
                        <tr style={{ textAlign: 'left', color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>
                            <th style={{ padding: '4px 6px' }}>Formando</th>
                            <th style={{ padding: '4px 6px' }}>Observação</th>
                            <th style={{ padding: '4px 6px' }}>Eficácia</th>
                            <th style={{ padding: '4px 6px' }}>Certificado</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {formandos.length === 0 && (
                            <tr><td colSpan={5} style={{ padding: '14px 6px', color: '#94a3b8' }}>Sem formandos registados.</td></tr>
                        )}
                        {formandos.map(f => {
                            const cor = EFICACIA_COR[f.eficacia] || { bg: '#fff', fg: '#334155' };
                            return (
                                <tr key={f.id} style={{ borderBottom: '1px solid #f8fafc' }}>
                                    <td style={{ padding: '4px 6px', fontWeight: 600, minWidth: 160 }}>{f.nome}</td>
                                    <td style={{ padding: '4px 6px' }}>
                                        <input defaultValue={f.observacao || ''} onBlur={e => { if (e.target.value !== (f.observacao || '')) atualizarFormando(f, { observacao: e.target.value }); }}
                                            style={{ ...inp, padding: '4px 8px', fontSize: '0.8rem', width: '100%' }} />
                                    </td>
                                    <td style={{ padding: '4px 6px' }}>
                                        <select value={f.eficacia || ''} onChange={e => atualizarFormando(f, { eficacia: e.target.value })}
                                            style={{ ...inp, padding: '4px 8px', fontSize: '0.8rem', fontWeight: 600, background: cor.bg, color: cor.fg }}>
                                            <option value="">—</option>
                                            <option value="Eficaz">Eficaz</option>
                                            <option value="Não Eficaz">Não Eficaz</option>
                                        </select>
                                    </td>
                                    <td style={{ padding: '4px 6px' }}>
                                        <select value={f.certificado || ''} onChange={e => atualizarFormando(f, { certificado: e.target.value })}
                                            style={{ ...inp, padding: '4px 8px', fontSize: '0.8rem' }}>
                                            <option value="">—</option>
                                            <option value="sim">Sim</option>
                                            <option value="não">Não</option>
                                        </select>
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                        <button data-liquid-secondary="true" onClick={() => removerFormando(f)} style={{ ...btnSec, padding: '2px 8px', fontSize: '0.75rem', color: '#e53e3e' }} aria-label="Apagar" title="Apagar"><DeleteIcon /></button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>

                <div style={{ display: 'flex', gap: 8, borderTop: '1px solid #f1f5f9', paddingTop: 10 }}>
                    <input value={novoNome} onChange={e => setNovoNome(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') adicionarFormando(); }}
                        placeholder="Nome do formando…" style={{ ...inp, flex: 1 }} />
                    <button data-liquid-primary="true" onClick={adicionarFormando} style={btnPri}>+ Adicionar</button>
                </div>
                {err && <div style={{ color: '#e53e3e', marginTop: 8, fontSize: '0.85rem' }}>{err}</div>}
            </div>
        </LiquidDialog>
    );
}

/* ---------- Fichas individuais (ISO 9001/45001 — 7.2) ----------
   Uma ficha por trabalhador, montada a partir do plano de formação. Não há
   lista paralela a manter: o que aparece na ficha é o que está no plano. */
function FichasIndividuais() {
    const [dados, setDados] = useState({ fichas: [], sem_ficha: [], ex_colaboradores: [] });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get('/sistema-integrado/formacao/fichas')
            .then(r => setDados(r.data || { fichas: [], sem_ficha: [], ex_colaboradores: [] }))
            .catch(e => console.error(e))
            .finally(() => setLoading(false));
    }, []);

    const abrirPdf = async (f) => {
        try {
            const r = await api.get(`/sistema-integrado/formacao/ficha/${f.id}/pdf`, { responseType: 'blob' });
            window.open(window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })), '_blank');
        } catch { alert('Erro ao gerar a ficha.'); }
    };

    if (loading) return <p style={{ color: '#718096' }}>A carregar…</p>;

    return (
        <div>
            {(dados.ex_colaboradores || []).length > 0 && (
                <div style={{ marginBottom: 14, padding: '8px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                    <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
                        <strong>{dados.ex_colaboradores.length} ex-colaboradores</strong> com formação registada em 2025:{' '}
                        {dados.ex_colaboradores.map(o => o.nome).join(' · ')}. O registo mantém-se por
                        ser histórico auditado; não têm ficha individual por já não pertencerem aos quadros.
                    </span>
                </div>
            )}

            {dados.sem_ficha.length > 0 && (
                <div data-liquid-notice="true" data-liquid-tone={liquidTone('#fef3c7')} style={{ marginBottom: 14, padding: '10px 14px', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8 }}>
                    <strong style={{ fontSize: '0.85rem', color: '#92400e' }}>
                        {dados.sem_ficha.length} formandos sem ficha de trabalhador
                    </strong>
                    <div style={{ fontSize: '0.8rem', color: '#92400e', marginTop: 4 }}>
                        {dados.sem_ficha.map(o => `${o.nome} (${o.n})`).join(' · ')}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#a16207', marginTop: 6 }}>
                        Têm formação registada e contrato em arquivo, mas não estão na lista de
                        trabalhadores da aplicação — por isso não é possível emitir-lhes ficha individual.
                    </div>
                </div>
            )}

            {dados.fichas.length === 0 ? (
                <div style={{ padding: '40px 0', textAlign: 'center', color: '#94a3b8' }}>
                    Sem formação registada em nome de trabalhadores.
                </div>
            ) : (
                <div className="table-responsive" style={{ backgroundColor: 'white', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' }}>
                    <table className="standard-table">
                        <thead>
                            <tr>
                                <th>Trabalhador</th>
                                <th>Função</th>
                                <th style={{ textAlign: 'center' }}>Ações</th>
                                <th style={{ textAlign: 'center' }}>Horas</th>
                                <th style={{ textAlign: 'center' }}>Eficazes</th>
                                <th style={{ textAlign: 'center' }}>Por avaliar</th>
                                <th style={{ textAlign: 'center' }}>Ficha</th>
                            </tr>
                        </thead>
                        <tbody>
                            {dados.fichas.map(f => (
                                <tr key={f.id}>
                                    <td style={{ fontWeight: 600 }}>{f.nome}</td>
                                    <td style={{ color: '#64748b' }}>{f.categoria_profissional || '—'}</td>
                                    <td style={{ textAlign: 'center' }}>{f.n_acoes}</td>
                                    <td style={{ textAlign: 'center' }}>{fmtNum(f.horas)}</td>
                                    <td style={{ textAlign: 'center' }}>
                                        <span data-liquid-tone={liquidTone('#dcfce7')} data-liquid-badge="true" style={{ ...badge, background: '#dcfce7', color: '#166534' }}>{f.n_eficazes}</span>
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                        {f.n_por_avaliar > 0
                                            ? <span data-liquid-tone={liquidTone('#fef3c7')} data-liquid-badge="true" style={{ ...badge, background: '#fef3c7', color: '#92400e' }}>{f.n_por_avaliar}</span>
                                            : <span style={{ color: '#cbd5e1' }}>—</span>}
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                        <button data-liquid-secondary="true" onClick={() => abrirPdf(f)} style={{ ...btnSec, ...mini }} title="Abrir ficha individual em PDF"><LiquidText>📄</LiquidText></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

/* ---------- Componente principal ---------- */
const Formacao = () => {
    const anoAtual = new Date().getFullYear();
    const [ano, setAno] = useState(anoAtual);
    const [anosExistentes, setAnosExistentes] = useState([]);
    const [acoes, setAcoes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editar, setEditar] = useState(null);
    const [eficacia, setEficacia] = useState(null);
    const [destin, setDestin] = useState(null);
    const [anexosDe, setAnexosDe] = useState(null);
    const [duplicando, setDuplicando] = useState(false);
    const [vista, setVista] = useState('plano');

    const carregar = useCallback(async () => {
        setLoading(true);
        try {
            const [r, ra] = await Promise.all([
                api.get('/sistema-integrado/formacao', { params: { ano } }),
                api.get('/sistema-integrado/formacao/anos'),
            ]);
            setAcoes(r.data || []);
            setAnosExistentes(Array.isArray(ra.data) ? ra.data : []);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, [ano]);
    useEffect(() => { carregar(); }, [carregar]);

    // Manter o modal de eficácia sincronizado com os dados recarregados
    useEffect(() => {
        if (eficacia) {
            const atualizada = acoes.find(a => a.id === eficacia.id);
            if (atualizada && atualizada !== eficacia) setEficacia(atualizada);
        }
    }, [acoes]); // intencional: só reagir a novos dados, não à própria seleção

    const anos = [];
    for (let a = anoAtual + 1; a >= anoAtual - 4; a--) anos.push(a);
    anosExistentes.forEach(a => { if (!anos.includes(a)) anos.push(a); });
    anos.sort((x, y) => y - x);

    const anoAnteriorComDados = anosExistentes.filter(a => a < ano).sort((x, y) => y - x)[0] || null;

    const eliminar = async (a) => {
        if (!window.confirm(`Eliminar a ação "${a.designacao}" e a sua avaliação de eficácia?`)) return;
        try { await api.delete(`/sistema-integrado/formacao/${a.id}`); carregar(); }
        catch { alert('Erro ao eliminar'); }
    };

    const duplicarAno = async () => {
        if (!anoAnteriorComDados) return;
        if (!window.confirm(`Copiar o plano de formação de ${anoAnteriorComDados} para ${ano} (sem formandos/eficácia)?`)) return;
        setDuplicando(true);
        try {
            const r = await api.post('/sistema-integrado/formacao/duplicar-ano', { de: anoAnteriorComDados, para: ano });
            alert(r.data.message);
            carregar();
        } catch (e) { alert(e.response?.data?.error || 'Erro ao duplicar'); }
        finally { setDuplicando(false); }
    };

    // Plano e fichas partilham a barra: a ficha é uma leitura do plano por
    // trabalhador, não um sítio separado onde se registe formação.
    const subnav = (
        <div className="shared-subnav" style={{ marginBottom: 14 }}>
            <button className={`shared-subnav-btn ${vista === 'plano' ? 'active' : ''}`}
                onClick={() => setVista('plano')}><LiquidText>📅 Plano de formação</LiquidText></button>
            <button className={`shared-subnav-btn ${vista === 'fichas' ? 'active' : ''}`}
                onClick={() => setVista('fichas')}><LiquidText>👤 Fichas individuais</LiquidText></button>
        </div>
    );

    if (vista === 'fichas') {
        return (<div>{subnav}<FichasIndividuais /></div>);
    }

    const totHoras = acoes.reduce((acc, a) => acc + (parseFloat(a.horas_totais) || 0), 0);
    const totOrc = acoes.reduce((acc, a) => acc + (parseFloat(a.orcamento) || 0), 0);
    const nRealizadas = acoes.filter(a => a.estado === 'realizada').length;

    return (
        <div>
            {subnav}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Ano do plano</label>
                        <select value={ano} onChange={e => setAno(parseInt(e.target.value, 10))} style={inp}>
                            {anos.map(a => <option key={a} value={a}>{a}{anosExistentes.includes(a) ? '' : ' (vazio)'}</option>)}
                        </select>
                    </div>
                    {acoes.length > 0 && (
                        <div style={{ display: 'flex', gap: 8, paddingBottom: 4, flexWrap: 'wrap' }}>
                            <span data-liquid-tone={liquidTone('#e0f2fe')} data-liquid-badge="true" style={{ ...badge, background: '#e0f2fe', color: '#075985' }}>{acoes.length} ações · {nRealizadas} realizadas</span>
                            <span data-liquid-tone={liquidTone('#f1f5f9')} data-liquid-badge="true" style={{ ...badge, background: '#f1f5f9', color: '#475569' }}>{fmtNum(totHoras)} h totais</span>
                            <span data-liquid-tone={liquidTone('#f1f5f9')} data-liquid-badge="true" style={{ ...badge, background: '#f1f5f9', color: '#475569' }}>{totOrc ? totOrc.toLocaleString('pt-PT', { style: 'currency', currency: 'EUR' }) : '— €'} orçamento</span>
                        </div>
                    )}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    {acoes.length === 0 && !loading && anoAnteriorComDados && (
                        <button data-liquid-secondary="true" onClick={duplicarAno} disabled={duplicando} style={btnSec}>
                            <LiquidText>{duplicando ? 'A copiar…' : `⧉ Copiar plano de ${anoAnteriorComDados}`}</LiquidText>
                        </button>
                    )}
                    <button data-liquid-primary="true" onClick={() => setEditar({})} style={btnPri}>+ Nova ação</button>
                </div>
            </div>

            {loading ? <p style={{ color: '#718096' }}>A carregar…</p>
                : acoes.length === 0 ? (
                    <div style={{ padding: '48px 0', textAlign: 'center', color: '#94a3b8' }}>
                        Sem plano de formação em {ano}. Crie uma ação ou copie o plano do ano anterior.
                    </div>
                ) : (
                    <div className="table-responsive" style={{ backgroundColor: 'white', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' }}>
                        <table className="standard-table">
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'center' }}>Nº</th>
                                    <th>Designação</th>
                                    <th>Destinatários</th>
                                    <th>Tipo</th>
                                    <th>Entidade / formador</th>
                                    <th style={{ textAlign: 'center' }}>Duração (h)</th>
                                    <th style={{ textAlign: 'center' }}>Formandos</th>
                                    <th style={{ textAlign: 'center' }}>Horas totais</th>
                                    <th>Meses</th>
                                    <th>Estado</th>
                                    <th style={{ textAlign: 'center' }}>Certificados</th>
                                    <th style={{ textAlign: 'right' }}>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {acoes.map(a => {
                                    const es = ESTADOS_ACAO[a.estado] || ESTADOS_ACAO.planeada;
                                    const mm = mesesArr(a.meses);
                                    const nEficaz = (a.formandos || []).filter(f => f.eficacia === 'Eficaz').length;
                                    const nAval = (a.formandos || []).filter(f => f.eficacia).length;
                                    return (
                                        <tr key={a.id}>
                                            <td style={{ textAlign: 'center', fontWeight: 700 }}>{a.numero || '—'}</td>
                                            <td style={{ fontWeight: 600, maxWidth: 260 }} title={a.objetivos || ''}>{a.designacao}</td>
                                            <td style={{ maxWidth: 210, fontSize: '0.83rem' }}>
                                                {(a.destinatarios_nomes || []).length > 0 ? (
                                                    <>
                                                        <button onClick={() => setDestin(a)} style={{ ...badge, background: '#e0f2fe', color: '#075985', border: 'none', cursor: 'pointer' }}
                                                            title={(a.destinatarios_nomes || []).map(d => d.nome).join('\n')}>
                                                            {a.destinatarios_nomes.length} nomeados
                                                        </button>
                                                        {/* Só depois de realizada é que faltar faz sentido:
                                                            numa ação planeada ninguém esteve presente ainda. */}
                                                        {a.estado === 'realizada' && (a.em_falta || []).length > 0 && (
                                                            <div style={{ marginTop: 4, fontSize: '0.72rem', color: '#92400e' }}
                                                                title={a.em_falta.join('\n')}><LiquidText>
                                                                ⚠ </LiquidText>{a.em_falta.length} sem presença registada
                                                            </div>
                                                        )}
                                                    </>
                                                ) : (
                                                    <button data-liquid-secondary="true" onClick={() => setDestin(a)} style={{ ...btnSec, padding: '3px 9px', fontSize: '0.75rem' }}
                                                        title={a.destinatarios || 'Sem destinatários'}>
                                                        <LiquidText>{a.destinatarios ? `${a.destinatarios.slice(0, 22)}… ✎` : '+ nomear'}</LiquidText>
                                                    </button>
                                                )}
                                            </td>
                                            <td style={{ whiteSpace: 'nowrap' }}>{a.tipo || '—'}</td>
                                            <td style={{ maxWidth: 160, fontSize: '0.83rem' }}>{a.entidade_formadora || '—'}</td>
                                            <td style={{ textAlign: 'center' }}>{fmtNum(a.duracao_horas)}</td>
                                            <td style={{ textAlign: 'center' }}>
                                                {(a.formandos || []).length || a.num_formandos || '—'}
                                                {nAval > 0 && <span style={{ color: '#94a3b8', fontSize: '0.76rem' }}> ({nEficaz}<LiquidText>✓)</LiquidText></span>}
                                            </td>
                                            <td style={{ textAlign: 'center' }}>{fmtNum(a.horas_totais)}</td>
                                            <td>
                                                <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap', maxWidth: 130 }}>
                                                    {mm.length === 0 ? <span style={{ color: '#cbd5e1' }}>—</span>
                                                        : mm.map(m => (
                                                            <span data-liquid-tone={liquidTone('#e0f2fe')} data-liquid-badge="true" key={m} style={{ ...badge, background: '#e0f2fe', color: '#075985', fontSize: '0.7rem', padding: '2px 6px' }}>{MESES[m - 1]}</span>
                                                        ))}
                                                </div>
                                            </td>
                                            <td><span data-liquid-tone={liquidTone(es.bg)} data-liquid-badge="true" style={{ ...badge, background: es.bg, color: es.fg }}><LiquidText>{es.label}</LiquidText></span></td>
                                            <td style={{ textAlign: 'center' }}>
                                                <button onClick={() => setAnexosDe(a)}
                                                    style={{ ...badge, border: 'none', cursor: 'pointer', background: (a.anexos || []).length ? '#dcfce7' : '#f1f5f9', color: (a.anexos || []).length ? '#166534' : '#94a3b8' }}
                                                    title={(a.anexos || []).length ? a.anexos.map(x => x.titulo).join('\n') : 'Sem certificado anexo'}>
                                                    {(a.anexos || []).length || '+'}
                                                </button>
                                            </td>
                                            <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                <button data-liquid-primary="true" onClick={() => setEficacia(a)} style={{ ...btnPri, ...mini }} title="Avaliação da eficácia (formandos)">Eficácia</button>
                                                <button data-liquid-secondary="true" onClick={() => setEditar({ acao: a })} style={{ ...btnSec, ...mini }} title="Editar">Editar</button>
                                                <button data-liquid-secondary="true" onClick={() => eliminar(a)} style={{ ...btnSec, ...mini, color: '#e53e3e' }} title="Eliminar"><DeleteIcon /></button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

            {editar && <AcaoModal acao={editar.acao} ano={ano} onClose={() => setEditar(null)} onSaved={carregar} />}
            {eficacia && <EficaciaModal acao={eficacia} onClose={() => setEficacia(null)} onChanged={carregar} />}
            {destin && <DestinatariosPicker acao={destin} onClose={() => setDestin(null)} onGravado={carregar} />}
            {anexosDe && <AnexosSgiModal rota="formacao" registo={anexosDe} etiqueta={`Ação ${anexosDe.numero || ''} — ${anexosDe.designacao}`} onClose={() => setAnexosDe(null)} onMudou={carregar} />}
        </div>
    );
};

/* ---------- estilos ---------- */
const lbl = { display: 'block', fontSize: '0.74rem', color: '#64748b', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.03em' };
const inp = { padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box' };
const badge = { padding: '3px 10px', borderRadius: 12, fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap' };
const btnSec = { padding: '7px 14px', border: '1px solid #cbd5e1', background: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem', color: '#334155' };
const btnPri = { padding: '7px 14px', border: '1px solid #3182ce', background: '#3182ce', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem' };
const mini = { padding: '5px 10px', marginLeft: 6, fontSize: '0.78rem' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '40px 16px', overflowY: 'auto' };
const modal = { background: '#fff', borderRadius: 14, padding: 22, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' };
const modalHead = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 };
const closeBtn = { border: 'none', background: 'transparent', fontSize: '1.2rem', cursor: 'pointer', color: '#64748b' };
const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 };
const grid3 = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 };
const grid4 = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10, marginBottom: 10 };

export default Formacao;
